import { httpsCallable } from 'firebase/functions';
import { fromUnknownError, RateLimitError } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * 投稿者本人から管理者への AI 再解析の依頼 (callable `requestPostReanalysis`、docs/13 §9-2)。
 *
 * 投稿者は AI 再解析を直接実行できない (aiAnalyzePost は管理者のみ)。依頼すると投稿に依頼が付き、
 * 管理画面の投稿一覧で管理者が拾って再解析する (再解析が済むと依頼は Functions が消す)。
 *
 *   - `surveyKey`: 選んでいる解析の観点 (docs/29)。既定の観点は null
 *   - `note`: 投稿者のひとこと (任意、200 字まで。Functions 側でも切り詰める)
 *   - 戻り値: `'requested'` (新しく依頼した) / `'already-requested'` (すでに依頼があった)
 *   - 失敗時は AppError を throw (呼出側で `formatErrorForToast` して表示)
 */
export const REANALYSIS_NOTE_MAX = 200;

export type RequestPostReanalysisStatus = 'requested' | 'already-requested';

export async function requestPostReanalysis(params: {
  districtId: string;
  postId: string;
  surveyKey: string | null;
  note: string;
}): Promise<RequestPostReanalysisStatus> {
  const note = Array.from(params.note.trim()).slice(0, REANALYSIS_NOTE_MAX).join('');
  try {
    const fn = httpsCallable<
      { districtId: string; postId: string; surveyKey: string | null; note: string | null },
      { status: RequestPostReanalysisStatus }
    >(functions, 'requestPostReanalysis');
    const res = await fn({
      districtId: params.districtId,
      postId: params.postId,
      surveyKey: params.surveyKey,
      note: note.length > 0 ? note : null,
    });
    return res.data?.status === 'already-requested' ? 'already-requested' : 'requested';
  } catch (e) {
    const err = fromUnknownError(e);
    // 上限は Functions が日本語の理由を返す。英語・コードだけのときに限り決まった文にする。
    if (err instanceof RateLimitError && !/[\u3040-\u30ff\u3400-\u9fff]/.test(err.message)) {
      throw new RateLimitError(
        err.code,
        '再解析の依頼が多すぎます。1 分ほど待ってからもう一度お試しください。',
        { cause: e, details: err.details },
      );
    }
    throw err;
  }
}
